import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { guardSurface } from '@/lib/platform/surfaces';
import { sessionSettled } from '@/lib/platform/auth';
import { resolveEntryRoute } from '@/lib/platform/routing';
import type { AppEntry } from '@/app/routes-types';
import { FrontDoorPage } from '@/features/front-door/FrontDoorPage';
import { NotFoundPage } from '@/features/front-door/NotFoundPage';
import { SignInPage } from '@/features/sign-in/SignInPage';
import { SignUpPage } from '@/features/sign-up/SignUpPage';
import { SetupPage } from '@/features/client/SetupPage';
import { IntakePage } from '@/features/filing/IntakePage';
import { FactsPage } from '@/features/filing/FactsPage';
import { ReviewPage } from '@/features/filing/ReviewPage';
import { SubmissionPage } from '@/features/filing/SubmissionPage';

export const entry: AppEntry = { byRole: { 'actor-cpa': '/filing-setup' } };
const rootRoute = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFoundPage });
const frontDoorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: FrontDoorPage,
  beforeLoad: async () => {
    const session = await sessionSettled();
    if (session.status === 'authed') {
      const to = resolveEntryRoute(entry, session.user?.roles ?? []);
      if (to !== '/') throw redirect({ to });
    }
    await guardSurface(entry, SURFACES['front-door']);
  },
  staticData: { title: 'Front Door', page: SURFACES['front-door'], standalone: true, isPublic: true },
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
  beforeLoad: async () => {
    const session = await sessionSettled();
    if (session.status === 'authed') throw redirect({ to: resolveEntryRoute(entry, session.user?.roles ?? []) });
  },
  staticData: { title: 'Sign In', page: SURFACES['sign-in'], standalone: true, isPublic: true },
});
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUpPage,
  beforeLoad: async () => {
    const session = await sessionSettled();
    if (session.status === 'authed') throw redirect({ to: resolveEntryRoute(entry, session.user?.roles ?? []) });
  },
  staticData: { title: 'Sign Up', page: SURFACES['sign-up'], standalone: true, isPublic: true },
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filing-setup',
  component: SetupPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-setup']),
  staticData: { title: 'Filing Setup', page: SURFACES['filing-setup'], standalone: false, isPublic: false },
});
const intakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/source-intake',
  component: IntakePage,
  beforeLoad: () => guardSurface(entry, SURFACES['source-intake']),
  staticData: { title: 'Source Intake', page: SURFACES['source-intake'], standalone: false, isPublic: false },
});
const factsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filing-facts',
  component: FactsPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-facts']),
  staticData: { title: 'Filing Facts', page: SURFACES['filing-facts'], standalone: false, isPublic: false },
});
const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/return-review',
  component: ReviewPage,
  beforeLoad: () => guardSurface(entry, SURFACES['return-review']),
  staticData: { title: 'Return Review', page: SURFACES['return-review'], standalone: false, isPublic: false },
});
const submissionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/filing-submission',
  component: SubmissionPage,
  beforeLoad: () => guardSurface(entry, SURFACES['filing-submission']),
  staticData: { title: 'Filing Submission', page: SURFACES['filing-submission'], standalone: false, isPublic: false },
});
export const router = createRouter({
  routeTree: rootRoute.addChildren([
    frontDoorRoute,
    signInRoute,
    signUpRoute,
    setupRoute,
    intakeRoute,
    factsRoute,
    reviewRoute,
    submissionRoute,
  ]),
});
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
